import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataTable } from '../../../../shared/components/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface WorkspaceSummary {
  readonly datasets: number;
  readonly analyticalDatasets: number;
  readonly forecastModels: number;
  readonly scenarioModels: number;
  readonly totalModels: number;
}

interface GovernancePosture {
  readonly totalDatasets: number;
  readonly catalogued: number;
  readonly certified: number;
  readonly withLineage: number;
  readonly qualityFailing: number;
  readonly byClassification: Readonly<Record<string, number>>;
}

interface DatasetRow {
  readonly datasetId: string;
  readonly datasetCode: string;
  readonly datasetName: string;
  readonly sourceDomain: string;
  readonly zone: string;
  readonly loadStatus: string;
  readonly qualityScore: string | null;
  readonly status: string;
}

/**
 * M41 — Data Lake Dashboard (§26). The read-only landing surface over the
 * enterprise data lake: the §13 data-science workspace headline counts, the §16
 * governance posture (cataloguing / certification / lineage / quality coverage,
 * BR-01/BR-02/BR-04) and a snapshot of the most recent datasets. Viewing the
 * workspace summary needs DATASCIENCE_VIEW; the posture needs GOVERNANCE_VIEW;
 * the dataset snapshot needs DATASET_VIEW. There is no mutate path here.
 */
export function DataLakeDashboard(): ReactNode {
  const { decimal } = useBusinessNumber();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [summary, setSummary] = useState<WorkspaceSummary | null>(null);
  const [posture, setPosture] = useState<GovernancePosture | null>(null);
  const [datasets, setDatasets] = useState<DatasetRow[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      apiRequest<{ data: WorkspaceSummary }>('/v1/data-lake/data-science/summary', { token }),
      apiRequest<{ data: GovernancePosture }>('/v1/data-lake/governance/posture', { token }),
      apiRequest<{ data: DatasetRow[]; total: number }>('/v1/data-lake/datasets?pageSize=10', { token }),
    ])
      .then(([s, p, d]) => {
        if (!alive) return;
        setSummary(s.data);
        setPosture(p.data);
        setDatasets(d.data);
        setTotal(d.total);
        setError(null);
      })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  return (
    <main aria-label="data-lake-dashboard">
      <h1>{t('m41.lake.title')}</h1>

      {error && <p role="alert" aria-label="dashboard-error">{error}</p>}
      {loading ? <p>{t('common.loading')}</p> : (
        <>
          {summary && (
            <section aria-label="workspace-summary">
              <h2>{t('m41.lake.workspace')}</h2>
              <ul>
                <li aria-label="kpi-datasets">{t('m41.lake.kpiDatasets', { n: summary.datasets })}</li>
                <li aria-label="kpi-analytical">{t('m41.lake.kpiAnalytical', { n: summary.analyticalDatasets })}</li>
                <li aria-label="kpi-forecasts">{t('m41.lake.kpiForecasts', { n: summary.forecastModels })}</li>
                <li aria-label="kpi-scenarios">{t('m41.lake.kpiScenarios', { n: summary.scenarioModels })}</li>
                <li aria-label="kpi-models">{t('m41.lake.kpiModels', { n: summary.totalModels })}</li>
              </ul>
            </section>
          )}

          {posture && (
            <section aria-label="governance-posture">
              <h2>{t('m41.lake.posture')}</h2>
              <ul>
                <li aria-label="posture-total">{t('m41.common.postureTotal', { n: posture.totalDatasets })}</li>
                <li aria-label="posture-catalogued">{t('m41.common.postureCatalogued', { n: posture.catalogued })}</li>
                <li aria-label="posture-certified">{t('m41.common.postureCertified', { n: posture.certified })}</li>
                <li aria-label="posture-lineage">{t('m41.common.postureLineage', { n: posture.withLineage })}</li>
                <li aria-label="posture-quality-failing">{t('m41.common.postureQualityFailing', { n: posture.qualityFailing })}</li>
              </ul>
              <ul aria-label="posture-classification">
                {Object.entries(posture.byClassification).map(([k, v]) => (
                  <li key={k} aria-label={`classification-${k}`}>{k}: {v}</li>
                ))}
              </ul>
            </section>
          )}

          <section aria-label="recent-datasets">
            <h2>{t('m41.lake.recent')}</h2>
            <p aria-label="datasets-total">{t('m41.common.datasetsCount', { count: total })}</p>
            <DataTable
              ariaLabel="datasets-table"
              rows={datasets}
              rowKey={(d) => d.datasetId}
              emptyMessage={t('m41.common.noDatasets')}
              columns={[
                { key: 'code', header: t('m41.common.code'), cellAriaLabel: (d) => `dataset-code-${d.datasetId}`, render: (d) => d.datasetCode },
                { key: 'name', header: t('m41.common.name'), render: (d) => d.datasetName },
                { key: 'domain', header: t('m41.common.domain'), render: (d) => d.sourceDomain },
                { key: 'zone', header: t('m41.common.zone'), render: (d) => d.zone },
                { key: 'load', header: t('m41.common.loadCol'), render: (d) => d.loadStatus },
                { key: 'quality', header: t('m41.common.quality'), render: (d) => decimal(d.qualityScore) },
                { key: 'status', header: t('m41.common.status'), render: (d) => d.status },
              ]}
            />
          </section>
        </>
      )}
    </main>
  );
}
